import { DAILY_AFFIRMATION_CONTENT_TITLE, MediaTypes, Persona, SubscriptionPlans } from '@/constants';
import Category from '@/models/category';
import { broadcastDailyAffirmationVideo, useContent, useCreateContent, useDeleteContent, useUpdateContent } from '@/services/contents';
import FormModal from '@/shared/components/form-modal';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useEffect, useRef, useState } from 'react';
import TextEditor from '@/shared/components/text-editor/TextEditor';
import CheckBoxField from '@/shared/components/form/CheckBox';
import { darkDangerText } from '@/shared/dark-inline-styles';

interface ContentModalProps {
    isOpen: boolean;
    selectedContentId?: string;
    toggle: () => void;
    categories: Category[];
}

const ContentModal = ({ isOpen, toggle, selectedContentId, categories }: ContentModalProps) => {
    const { data: content, isLoading } = useContent(selectedContentId as string, isOpen);
    const { mutateAsync: createContentAsync, isPending: isCreating } = useCreateContent();
    const { mutateAsync: updateContentAsync, isPending: isUpdating } = useUpdateContent();
    const { mutateAsync: deleteContentAsync, isPending: isDeleting } = useDeleteContent();

    const [title, setTitle] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [description, setDescription] = useState('');
    const [body, setBody] = useState('');
    const [initialBody, setInitialBody] = useState('');
    const [completionCaption, setCompletionCaption] = useState<string | undefined>('');
    const [imagePath, setImagePath] = useState('');
    const [imageLink, setImageLink] = useState('');
    const [mediaPath, setMediaPath] = useState('');
    const [mediaLink, setMediaLink] = useState('');
    const [mediaType, setMediaType] = useState<MediaTypes>(MediaTypes.Video);
    const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlans>(SubscriptionPlans.Free);
    const [introSeconds, setIntroSeconds] = useState<number>();
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [isBroadcasting, setIsBroadcasting] = useState(false);
    // Holds the API error message (e.g. "title is required and cannot be empty"
    // when the backend's notEmpty validator rejects a blank save). Without
    // this, the API's 400 silently keeps the modal open with no signal to
    // Saba about what to fix.
    const [error, setError] = useState<string | null>(null);
    // In-flight media uploads (cover image + media). Save is blocked while > 0
    // so content can't be saved with a half-uploaded / missing media reference.
    const [uploadingCount, setUploadingCount] = useState(0);
    const onUploadingChange = (uploading: boolean) => setUploadingCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    const isUploading = uploadingCount > 0;
    const videoFileRef = useRef<File | null>(null);

    const resetStates = () => {
        setTitle('');
        setCategoryId('');
        setPersonas([]);
        setDescription('');
        setIntroSeconds(undefined);
        setBody('');
        setInitialBody('');
        setCompletionCaption('');
        setImagePath('');
        setImageLink('');
        setMediaPath('');
        setMediaLink('');
        setMediaType(MediaTypes.Video);
        setSubscriptionPlan(SubscriptionPlans.Free);
        setVideoFile(null);
        setError(null);
        setUploadingCount(0);
    };

    // `isOpen` in the deps so re-opening the SAME content re-fills the form from
    // the (cached) data — otherwise the effect never re-fires on re-open and the
    // close-reset below leaves it blank.
    useEffect(() => {
        if (!isOpen) return;
        if (content?.data) {
            setTitle(content.data.title);
            setCategoryId(content.data.categoryId);
            setPersonas(content.data.personas || []);
            setDescription(content.data.description);
            setIntroSeconds(content.data.introSeconds);
            setBody(content.data.body);
            setInitialBody(content.data.body);
            setCompletionCaption(content.data.completionCaption);
            setImagePath(content.data.imagePath);
            setImageLink(content.data.imageLink);
            setMediaLink(content.data.mediaLink);
            setMediaPath(content.data.mediaPath);
            setMediaType(content.data.mediaType);
            setSubscriptionPlan(content.data.subscriptionPlan);
        } else if (!selectedContentId) {
            resetStates();
        }
    }, [isOpen, content?.data, selectedContentId]);

    useEffect(() => {
        if (!isOpen) {
            resetStates();
        }
    }, [isOpen]);

    const sendVideoToTelegramBot = async (contentId: string) => {
        const file = videoFileRef.current;
        if (!file || title.trim().toLowerCase() !== DAILY_AFFIRMATION_CONTENT_TITLE.toLowerCase()) return;

        setIsBroadcasting(true);
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const result = reader.result as string;
                    resolve(result.split(',')[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

            await broadcastDailyAffirmationVideo({
                videoBase64: base64,
                fileName: file.name,
                contentId,
                contentTitle: title,
                imageUrl: imageLink,
                mediaLink,
            });
        } catch (error) {
            console.error('Failed to broadcast daily affirmation video to Telegram:', error);
        } finally {
            setIsBroadcasting(false);
        }
    };

    // Client-side guard for the fields whose emptiness silently breaks the app —
    // a cleared Category makes the content vanish from the SET page (the app
    // queries by categoryId). The API rejects these too (defense in depth); this
    // just gives a friendly message before the request.
    const validate = (): string | null => {
        if (!title.trim()) return 'Title is required';
        if (!categoryId) return 'Category is required';
        // A missing cover image is not an error — some content legitimately has none —
        // but it IS invisible: the app's Learn card renders no <img> at all when
        // imagePath is empty, so the content quietly appears without artwork and
        // looks broken to whoever uploaded it. Warn once, and let them proceed.
        return null;
    };

    /** Non-blocking heads-up, shown next to the field rather than as an error. */
    const coverImageMissing = !imagePath;

    const handleSave = async () => {
        if (isUploading) {
            setError('Please wait — media is still uploading.');
            return;
        }
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        // `callApi` (services/api.ts) NEVER throws on API-level errors — it
        // resolves with `{ status: 'error', error: { message } }`. So a
        // try/catch alone misses 400/409/etc. We explicitly inspect the
        // response status and surface the backend's `error.message`. This is
        // how Saba sees the new "title is required and cannot be empty" 400
        // from updateContentSchema's notEmpty guard.
        setError(null);
        try {
            if (selectedContentId) {
                const result = await updateContentAsync({
                    contentId: selectedContentId,
                    title,
                    categoryId,
                    description,
                    body,
                    imagePath,
                    mediaPath,
                    mediaType,
                    subscriptionPlan,
                    personas,
                    completionCaption,
                    introSeconds,
                });
                if (result.status === 'error') {
                    setError(result.error?.message ?? 'Failed to update content.');
                    return;
                }
                videoFileRef.current = videoFile;
                await sendVideoToTelegramBot(selectedContentId);
                toggle();
            } else {
                const result = await createContentAsync({
                    title,
                    categoryId,
                    description,
                    body,
                    imagePath,
                    mediaPath,
                    mediaType,
                    subscriptionPlan,
                    personas,
                    completionCaption,
                    introSeconds,
                });
                if (result.status === 'error') {
                    setError(result.error?.message ?? 'Failed to create content.');
                    return;
                }
                toggle();
            }
        } catch (e) {
            // Network/unexpected thrown errors only — callApi handles HTTP errors.
            console.error('Unexpected error saving content:', e);
            setError(e instanceof Error ? e.message : 'Unexpected error saving content.');
        }
    };

    const handleDelete = async () => {
        if (!selectedContentId) return;
        setError(null);
        try {
            const result = await deleteContentAsync({ contentId: selectedContentId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete content.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Unexpected error deleting content:', e);
            setError(e instanceof Error ? e.message : 'Unexpected error deleting content.');
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedContentId}
            title={`${selectedContentId ? 'Edit' : 'Add'} Content`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && (
                        <div
                            role="alert"
                            style={{
                                background: 'rgba(255, 107, 107, 0.12)',
                                border: '1px solid rgba(255, 107, 107, 0.35)',
                                color: darkDangerText,
                                padding: 10,
                                borderRadius: 4,
                                marginBottom: 12,
                            }}
                        >
                            {error}
                        </div>
                    )}
                    <MaterialTextField label="Title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <MaterialTextField name="select" select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                        <MaterialFormOption value="">— Select a category —</MaterialFormOption>
                        {categories.map((c) => (
                            <MaterialFormOption key={c.id} value={c.id}>
                                {c.name}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    <div style={{ marginBottom: 20 }}>
                        <p style={{ marginBottom: 8 }}>Personas</p>
                        <div style={{ display: 'flex', gap: 24 }}>
                            {Object.values(Persona).map((persona) => (
                                <div key={persona} style={{ display: 'flex', alignItems: 'center' }}>
                                    <CheckBoxField
                                        name={`persona_${persona}`}
                                        checked={personas.includes(persona)}
                                        onChange={() =>
                                            setPersonas((prev) => (prev.includes(persona) ? prev.filter((p) => p !== persona) : [...prev, persona]))
                                        }
                                        label={persona.charAt(0).toUpperCase() + persona.slice(1)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <MaterialTextField
                        name="select"
                        select
                        label="Subscription Plan"
                        value={subscriptionPlan}
                        onChange={(e) => setSubscriptionPlan(e.target.value as SubscriptionPlans)}
                    >
                        <MaterialFormOption value={SubscriptionPlans.Free}>{SubscriptionPlans.Free}</MaterialFormOption>
                        <MaterialFormOption value={SubscriptionPlans.Premium}>{SubscriptionPlans.Premium}</MaterialFormOption>
                    </MaterialTextField>
                    <MaterialTextField
                        label="Description"
                        type="text"
                        multiline
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <div>
                        <p>Body</p>
                        <TextEditor initialContent={initialBody} onChange={(content) => setBody(content)} />
                    </div>
                    <MaterialTextField
                        label="Completion Caption"
                        type="text"
                        multiline
                        value={completionCaption}
                        onChange={(e) => setCompletionCaption(e.target.value)}
                    />
                    <div>
                        <p>Cover Image</p>
                        <DropZoneField
                            customHeight
                            accept="image/jpeg, image/jpg, image/png"
                            currentValue={imageLink}
                            currentFileType="image"
                            onChange={(imagePath) => setImagePath(imagePath)}
                            onUploadingChange={onUploadingChange}
                        />
                        {coverImageMissing && (
                            <p style={{ marginTop: 6, fontSize: 12, color: '#e0a458' }}>
                                No cover image. The content will still publish, but it shows without artwork on the app’s Learn page.
                            </p>
                        )}
                    </div>
                    <div className="mt-3">
                        <p>Media</p>
                        <DropZoneField
                            customHeight
                            accept="audio/mp3, audio/mpeg, video/mp4"
                            currentValue={mediaLink}
                            currentFileType={mediaType}
                            onChange={(mediaPath, mediaType, file) => {
                                setMediaPath(mediaPath);
                                setMediaType(mediaType.split('/')[0].trim() as MediaTypes);
                                setVideoFile(file?.type?.startsWith('video/') ? file : null);
                            }}
                            onUploadingChange={onUploadingChange}
                            description={description}
                        />
                    </div>
                    <MaterialTextField
                        label="Intro Seconds (only fill if the content is video and want to skip intro)"
                        type="number"
                        value={introSeconds}
                        onChange={(e) => setIntroSeconds(Number(e.target.value))}
                    />
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting || isBroadcasting}
            saveDisabled={isUploading}
            saveDisabledHint="Uploading media… please wait until it finishes before saving."
        />
    );
};

export default ContentModal;
